import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'
import { serverMessage } from '@/Ship/Helpers/serverError'

import type { UserLike } from '@/Ship/Helpers/fieldViews'

/**
 * Межа тексту коментаря — та сама, що в Ship `AbstractCommentEntity` беку
 * (`Length ≤ 20 000`, ADR 027). Форма не дає ввести більше, ніж бек прийме.
 */
export const COMMENT_TEXT_MAX_LENGTH = 20000

/** Скільки коментарів тягнемо за раз; решту — кнопкою «Показати ще». */
export const COMMENTS_PAGE_SIZE = 20

/** Текст валідації порожнього коментаря — дослівно з легасі. */
export const COMMENT_REQUIRED_MESSAGE = 'Напишіть коментар.'

/** Коли сервер не пояснив відмову. */
const FALLBACK_ERROR = 'Зверніться до адміністратора системи.'

/** Права коментаря: автор або адміністратор, власник не видалений (`CommentRule`). */
export interface CommentPermissions {
    read?: boolean
    update?: boolean
    delete?: boolean
}

/** Коментар так, як його віддає колекція `…_comments` (група `comment:read`). */
export interface FeedComment {
    uuid: string
    '@id'?: string
    text?: string | null
    createdAt?: string | null
    updatedAt?: string | null
    createdBy?: UserLike | null
    _permissions?: CommentPermissions
}

type CollectionResponse =
    | { items?: FeedComment[]; 'hydra:member'?: FeedComment[]; meta?: { total?: number } }
    | FeedComment[]
    | null

const itemsOf = (data: CollectionResponse): FeedComment[] => {
    const items = Array.isArray(data) ? data : (data?.items ?? data?.['hydra:member'] ?? [])

    return Array.isArray(items) ? items.filter((item) => typeof item?.uuid === 'string') : []
}

const totalOf = (data: CollectionResponse, fallback: number): number =>
    !Array.isArray(data) && typeof data?.meta?.total === 'number' ? data.meta.total : fallback

/**
 * Стор стрічки коментарів одного власника (клієнт, контакт, перевізник…),
 * ADR 027. Екземпляр — на змонтовану стрічку.
 *
 * Коментарі кожного власника живуть у своїй колекції (`/services/client_comments`,
 * `/services/client_contact_comments`): стор отримує її адресу й назву поля
 * власника, а відбирає записи префільтром `<поле>.uuid`. Порядок — новіші
 * зверху, як віддає бек.
 *
 * Права — **лише** з `_permissions` кожного коментаря (`?permissions=true`):
 * змінити й видалити може автор або адміністратор, а коментар видаленого
 * власника — лише читати. Ролей стор не знає.
 *
 * Помилки сервера стор тримає сам (`draftError`, `editingError`) — текстом
 * `serverMessage`, а не технічним слідом HTTP-клієнта.
 */
export class CommentsFeedStore {
    items: FeedComment[] = []
    /** Скільки коментарів усього — якщо більше, ніж показано, стрічка про це скаже */
    total = 0
    isLoading = false
    isLoadingMore = false
    /** Запит не вдався: порожня стрічка тоді означала б неправду «коментарів немає» */
    failed = false

    draft = ''
    draftError: string | null = null
    isSubmitting = false

    editingUuid: string | null = null
    editingText = ''
    editingError: string | null = null
    isSavingEdit = false

    /** Коментар, який саме видаляється: його кнопка крутиться, решта — ні */
    removingUuid: string | null = null

    private page = 0
    private ownerUuid: string | undefined

    constructor(
        private readonly restUrl: string,
        private readonly ownerField: string
    ) {
        makeAutoObservable(this, {}, { autoBind: true })
    }

    get hasMore(): boolean {
        return this.items.length < this.total
    }

    /** Перша сторінка стрічки власника; чернетка й редагування скидаються. */
    async load(ownerUuid: string | undefined): Promise<void> {
        this.ownerUuid = ownerUuid
        this.items = []
        this.total = 0
        this.page = 0
        this.failed = false
        this.draft = ''
        this.draftError = null
        this.cancelEdit()

        if (!ownerUuid) return

        this.isLoading = true

        try {
            const data = await this.fetchPage(ownerUuid, 1)

            runInAction(() => {
                if (this.ownerUuid !== ownerUuid) return

                this.items = itemsOf(data)
                this.total = totalOf(data, this.items.length)
                this.page = 1
            })
        } catch {
            runInAction(() => {
                this.failed = true
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    /** Наступна сторінка — у кінець стрічки, без дублів (новий коментар зсуває сторінки). */
    async loadMore(): Promise<void> {
        const ownerUuid = this.ownerUuid

        if (!ownerUuid || !this.hasMore || this.isLoadingMore) return

        this.isLoadingMore = true

        try {
            const data = await this.fetchPage(ownerUuid, this.page + 1)

            runInAction(() => {
                if (this.ownerUuid !== ownerUuid) return

                const known = new Set(this.items.map((item) => item.uuid))

                this.items = [...this.items, ...itemsOf(data).filter((item) => !known.has(item.uuid))]
                this.total = totalOf(data, this.total)
                this.page += 1
            })
        } catch {
            runInAction(() => {
                this.failed = true
            })
        } finally {
            runInAction(() => {
                this.isLoadingMore = false
            })
        }
    }

    setDraft(value: string): void {
        this.draft = value
        this.draftError = null
    }

    /**
     * Додає коментар від імені поточного користувача. `ownerIri` — `@id`
     * власника: ядро беку приймає зв'язок саме IRI.
     */
    async submit(ownerIri: string | undefined): Promise<boolean> {
        const text = this.draft.trim()

        if (text === '') {
            this.draftError = COMMENT_REQUIRED_MESSAGE

            return false
        }

        if (!ownerIri || this.isSubmitting) return false

        this.isSubmitting = true
        this.draftError = null

        try {
            const raw = await restClient.post(`${this.restUrl}?permissions=true`, {
                json: { [this.ownerField]: ownerIri, text },
            })
            const created = raw as unknown as FeedComment

            runInAction(() => {
                if (typeof created?.uuid === 'string') {
                    this.items = [created, ...this.items.filter((item) => item.uuid !== created.uuid)]
                    this.total += 1
                }

                this.draft = ''
            })

            return true
        } catch (error) {
            runInAction(() => {
                this.draftError = serverMessage(error, FALLBACK_ERROR)
            })

            return false
        } finally {
            runInAction(() => {
                this.isSubmitting = false
            })
        }
    }

    startEdit(comment: FeedComment): void {
        this.editingUuid = comment.uuid
        this.editingText = comment.text ?? ''
        this.editingError = null
    }

    setEditingText(value: string): void {
        this.editingText = value
        this.editingError = null
    }

    cancelEdit(): void {
        this.editingUuid = null
        this.editingText = ''
        this.editingError = null
    }

    async saveEdit(): Promise<boolean> {
        const uuid = this.editingUuid
        const text = this.editingText.trim()

        if (!uuid || this.isSavingEdit) return false

        if (text === '') {
            this.editingError = COMMENT_REQUIRED_MESSAGE

            return false
        }

        this.isSavingEdit = true

        try {
            const raw = await restClient.patch(`${this.restUrl}/${encodeURIComponent(uuid)}?permissions=true`, {
                json: { text },
            })
            const updated = raw as unknown as FeedComment

            runInAction(() => {
                this.items = this.items.map((item) =>
                    item.uuid === uuid ? (typeof updated?.uuid === 'string' ? updated : { ...item, text }) : item
                )
                this.cancelEdit()
            })

            return true
        } catch (error) {
            runInAction(() => {
                this.editingError = serverMessage(error, FALLBACK_ERROR)
            })

            return false
        } finally {
            runInAction(() => {
                this.isSavingEdit = false
            })
        }
    }

    /**
     * Видаляє коментар (жорстко, ADR 027). Підтвердження — справа стрічки
     * (`Popconfirm`); тут — лише запит. Повертає текст помилки або `null`.
     */
    async remove(uuid: string): Promise<string | null> {
        if (this.removingUuid !== null) return null

        this.removingUuid = uuid

        try {
            await restClient.delete(`${this.restUrl}/${encodeURIComponent(uuid)}`)

            runInAction(() => {
                const before = this.items.length

                this.items = this.items.filter((item) => item.uuid !== uuid)
                this.total = Math.max(0, this.total - (before - this.items.length))

                if (this.editingUuid === uuid) this.cancelEdit()
            })

            return null
        } catch (error) {
            return serverMessage(error, FALLBACK_ERROR)
        } finally {
            runInAction(() => {
                this.removingUuid = null
            })
        }
    }

    /**
     * `forceRefresh`: ядровий клієнт дедуплікує GET на 30 с, і без нього
     * повторно відкрита картка показала б стару стрічку.
     */
    private async fetchPage(ownerUuid: string, page: number): Promise<CollectionResponse> {
        const filters = JSON.stringify({ and: [{ field: `${this.ownerField}.uuid`, op: '=', value: ownerUuid }] })
        const raw = await restClient.get(
            `${this.restUrl}?limit=${COMMENTS_PAGE_SIZE}&page=${page}&permissions=true&filters=${encodeURIComponent(filters)}`,
            undefined,
            { forceRefresh: true }
        )

        return raw as unknown as CollectionResponse
    }
}
